// Roll20-style text expansion done before a chat message is sent:
//   #macro            → macro body
//   %{Char|ability}   → character ability body (bare @{x} inside it bind to Char)
//   %{selected|abil}  → ability of the selected token's character
//   @{Char|attr}      @{Char|attr|max}   @{selected|attr}   @{attr} (speaking-as context)
//   @{selected|bar1}  @{selected|token_name}  @{selected|token_id}
//   ?{Question|default}  ?{Question|Label,value|Label2,value2}
import type { Char, Macro, Obj } from "./types";
import { allAttrs } from "./sheet5e";

export interface ExpandCtx {
  chars: Char[];
  macros: Macro[];
  sheet: string;
  asChar?: Char | null;
  selected?: Obj | null;
  ask?: (question: string, options: { label: string; value: string }[] | null, dflt: string) => Promise<string | null>;
}

export class ExpandCancelled extends Error {}

const findChar = (ctx: ExpandCtx, key: string): Char | undefined => {
  if (key.startsWith("#")) return ctx.chars.find(c => c.id === key.slice(1));
  const k = key.trim().toLowerCase();
  return ctx.chars.find(c => c.name.trim().toLowerCase() === k) ?? ctx.chars.find(c => c.id === key);
};

function tokenAttr(tok: Obj, name: string, max: boolean): string | undefined {
  const m = name.match(/^bar([123])(?:_(max|value))?$/);
  if (m) {
    const b = tok.bars?.[+m[1] - 1];
    return (max || m[2] === "max" ? b?.max : b?.v) ?? "";
  }
  if (name === "token_name") return tok.name ?? "";
  if (name === "token_id") return tok.id;
  return undefined;
}

function charAttr(ctx: ExpandCtx, ch: Char, name: string, max: boolean): string | undefined {
  const all = allAttrs(ch, ctx.sheet);
  if (max) return all[`${name}_max`];
  return all[name];
}

export async function expand(text: string, ctx: ExpandCtx): Promise<{ text: string; warnings: string[] }> {
  const warnings: string[] = [];
  let s = text;
  for (let pass = 0; pass < 12; pass++) {
    const before = s;
    // macros (only names that exist, so "#1" stays literal)
    s = s.replace(/#([A-Za-z0-9_\-]+)/g, (m, name) => {
      const mac = ctx.macros.find(x => x.name === name);
      return mac ? mac.action : m;
    });
    // abilities
    s = s.replace(/%\{([^|}]+)\|([^}]+)\}/g, (m, who, abil) => {
      let ch: Char | undefined;
      if (who === "selected") ch = ctx.selected?.represents ? findChar(ctx, "#" + ctx.selected.represents) : undefined;
      else ch = findChar(ctx, who);
      if (!ch) { warnings.push(`No character "${who}" for ability ${abil}`); return ""; }
      const ab = ch.abilities?.find(a => a.name.toLowerCase() === abil.trim().toLowerCase());
      if (!ab) { warnings.push(`${ch.name} has no ability "${abil}"`); return ""; }
      return bindBare(ab.action, ch.id);
    });
    // attributes
    s = s.replace(/@\{([^}]+)\}/g, (m, body: string) => {
      const p = body.split("|");
      let ch: Char | undefined;
      let name: string, max = false;
      if (p.length === 1) {
        ch = ctx.asChar ?? undefined;
        name = p[0];
      } else {
        name = p[1];
        max = p[2] === "max";
        if (p[0] === "selected") {
          const tok = ctx.selected;
          if (!tok) { warnings.push(`No token selected for @{${body}}`); return "0"; }
          const tv = tokenAttr(tok, name.trim(), max);
          if (tv !== undefined) return tv;
          ch = tok.represents ? findChar(ctx, "#" + tok.represents) : undefined;
          if (!ch) { warnings.push(`Selected token has no character for @{${body}}`); return "0"; }
        } else if (p[0] === "target") {
          warnings.push("@{target|…} isn't supported — use @{selected|…}");
          return "0";
        } else ch = findChar(ctx, p[0]);
      }
      if (!ch) { warnings.push(`No character for @{${body}}`); return "0"; }
      name = name.trim();
      const v = charAttr(ctx, ch, name, max);
      if (v === undefined || v === "") {
        if (name === "character_name") return ch.name;
        warnings.push(`${ch.name} has no attribute "${name}${max ? "|max" : ""}"`);
        return "0";
      }
      return bindBare(String(v), ch.id);
    });
    if (s === before) break;
  }
  // queries — identical questions reuse the first answer
  const answers = new Map<string, string>();
  const re = /\?\{([^}]*)\}/;
  for (let guard = 0; guard < 30; guard++) {
    const m = s.match(re);
    if (!m) break;
    const parts = m[1].split("|");
    const q = parts[0] || "Value";
    let ans = answers.get(q);
    if (ans === undefined) {
      let options: { label: string; value: string }[] | null = null;
      let dflt = "";
      if (parts.length > 2 || (parts.length === 2 && parts[1].includes(","))) {
        options = parts.slice(1).map(o => {
          const i = o.indexOf(",");
          return i < 0 ? { label: o, value: o } : { label: o.slice(0, i), value: o.slice(i + 1) };
        });
      } else dflt = parts[1] ?? "";
      const got = ctx.ask ? await ctx.ask(q, options, dflt) : options ? options[0].value : dflt;
      if (got === null) throw new ExpandCancelled();
      ans = got;
      answers.set(q, ans);
    }
    s = s.slice(0, m.index!) + ans + s.slice(m.index! + m[0].length);
  }
  return { text: s, warnings };
}

// @{x} (bare) inside a character-owned text → @{#id|x}
function bindBare(text: string, charId: string): string {
  return text.replace(/@\{([^}|]+)\}/g, (_m, name) => `@{#${charId}|${name}}`).replace(/%\{([^}|]+)\}/g, (_m, n) => `%{#${charId}|${n}}`);
}
